import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/Basic/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupUmpireLeagues } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedLeagues: SelectedItemDto[] | undefined;
  onChange: (leagues: SelectedItemDto[]) => void;
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
};

const SelectUmpireLeagues = ({ selectedLeagues, onChange, dateRanges }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePostLookupUmpireLeagues(dateRanges);

  const allLeagueIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);
  const getLeagueName = useCallback(
    (leagueId: string) => new Map(data?.map((datum) => [datum.id, datum.name])).get(leagueId) ?? (t('Nonexistent league') as string),
    [data]
  );

  return (
    <AutocompleteWithChips
      label={t('Leagues')}
      options={allLeagueIds}
      selected={selectedLeagues?.map((league) => league.id) ?? []}
      setSelected={(selectedIds) =>
        onChange(selectedIds.map((id) => data?.find((datum) => datum.id === id)).filter(Boolean) as SelectedItemDto[])
      }
      getOptionLabel={(option) => (isLoading ? t('Loading leagues...') : getLeagueName(option))}
      noOptionsText={isLoading ? t('Loading leagues...') : t('No leagues found')}
      disabled={!dateRanges}
    />
  );
};

export default SelectUmpireLeagues;
